import {
  Component,
  HostBinding,
  Input,
  OnInit,
  OnChanges,
  Output,
  EventEmitter,
  OnDestroy,
} from '@angular/core';
import { NavItem } from './nav-item';
import { NavigationEnd, Router } from '@angular/router';
import { NavService } from '../../../../../services/nav.service';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { TranslateModule } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MaterialModule } from 'src/app/material.module';
import { CommonModule } from '@angular/common';
import { PermissionsService } from '../../../../../services/permissions/permissions.service';
import { AuthenticationService } from '../../../../../services/apps/Authentication/authentication.service';
import { NavItemVisibilityService } from '../../../../../services/nav-item-visibility.service';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-nav-item',
  imports: [TranslateModule, TablerIconsModule, MaterialModule, CommonModule],
  templateUrl: './nav-item.component.html',
  styleUrls: [],
  animations: [
    trigger('indicatorRotate', [
      state('collapsed', style({ transform: 'rotate(0deg)' })),
      state('expanded', style({ transform: 'rotate(180deg)' })),
      transition(
        'expanded <=> collapsed',
        animate('225ms cubic-bezier(0.4,0.0,0.2,1)')
      ),
    ]),
  ],
})
export class AppNavItemComponent implements OnChanges, OnInit, OnDestroy {
  @Output() toggleMobileLink: any = new EventEmitter<void>();
  @Output() notify: EventEmitter<boolean> = new EventEmitter<boolean>();

  expanded: any = false;
  disabled: any = false;
  twoLines: any = false;
  hasPermission: boolean = true;
  private destroy$ = new Subject<void>();
  
  @HostBinding('attr.aria-expanded') ariaExpanded = this.expanded;
  @Input() item: NavItem | any;
  @Input() depth: any;

  constructor(
    public navService: NavService,
    public router: Router,
    private permissionsService: PermissionsService,
    private authService: AuthenticationService,
    private navItemVisibility: NavItemVisibilityService
  ) {
    if (this.depth === undefined) {
      this.depth = 0;
    }
  }

  ngOnInit(): void {
    // Verificar permisos inmediatamente
    this.checkPermission();

    this.authService.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.checkPermission());
    
    // Suscribirse a cambios en los permisos
    this.permissionsService.permissions$
      .pipe(takeUntil(this.destroy$))
      .subscribe((permissions) => {
        console.log(`[NavItem] Permisos actualizados para "${this.item?.displayName}":`, permissions);
        this.checkPermission();
      });

    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.syncExpandedFromUrl());
    this.syncExpandedFromUrl();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private checkPermission(): void {
    this.hasPermission = this.navItemVisibility.isVisible(this.item);
  }

  isItemActive(nav: NavItem): boolean {
    if (nav.activeRoutePrefixes?.length) {
      const path = this.router.url.split('?')[0].split('#')[0];
      return nav.activeRoutePrefixes.some((p) => path === p || path.startsWith(p + '/'));
    }
    if (!nav.route) {
      return false;
    }
    const route = nav.route.startsWith('/') ? nav.route : `/${nav.route}`;
    return this.router.isActive(route, true);
  }

  ngOnChanges() {
    this.syncExpandedFromUrl();
  }

  private syncExpandedFromUrl(): void {
    const url =
      this.navService.currentUrl() ??
      this.router.url.split('?')[0].split('#')[0];
    if (!url) {
      return;
    }
    if (this.item.route) {
      const normalized = this.item.route.startsWith('/') ? this.item.route : `/${this.item.route}`;
      this.expanded = url.startsWith(normalized);
    } else if (this.item.children && this.item.children.length) {
      const childMatch = this.item.children.some((child: NavItem) => {
        if (!child.route) return false;
        const childRoute = child.route.startsWith('/') ? child.route : `/${child.route}`;
        return url.startsWith(childRoute);
      });
      if (childMatch) {
        this.expanded = true;
      } else if (this.item.defaultExpanded === true) {
        this.expanded = true;
      } else if (this.item.defaultExpanded === false) {
        this.expanded = false;
      } else {
        this.expanded = false;
      }
    }
    this.ariaExpanded = this.expanded;
  }

  onItemSelected(item: NavItem) {
    if (!item.children || !item.children.length) {
      console.log('Navegando a ruta principal:', item.route);
      
      // Verificar si la ruta existe
      if (item.route) {
        // Asegurarnos de que la ruta comience con / para navegación absoluta
        const route = item.route.startsWith('/') ? item.route : `/${item.route}`;
        console.log('Ruta normalizada:', route);
        this.router.navigate([route]);
      } else {
        console.warn('El elemento del menú no tiene una ruta definida:', item.displayName);
      }
    }
    if (item.children && item.children.length) {
      console.log('Expandiendo/colapsando menú con hijos:', item.displayName);
      this.expanded = !this.expanded;
    }
    //scroll
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
    if (!this.expanded) {
      if (window.innerWidth < 1024) {
        console.log('Notificando cierre de sidenav en vista móvil');
        this.notify.emit();
      }
    }
  }  onSubItemSelected(item: NavItem) {
    if (!item.children || !item.children.length) {
      console.log('Navegando a ruta secundaria:', item.route);
      
      // Verificar si la ruta existe
      if (item.route) {
        // Asegurarnos de que la ruta comience con / para navegación absoluta
        const route = item.route.startsWith('/') ? item.route : `/${item.route}`;
        console.log('Ruta secundaria normalizada:', route);
        this.router.navigate([route]);
      } else {
        console.warn('El elemento del submenú no tiene una ruta definida:', item.displayName);
      }
      
      // Si estamos en vista móvil y el menú está expandido, cerramos el sidenav
      if (this.expanded && window.innerWidth < 1024) {
        console.log('Cerrando sidenav después de navegación secundaria');
        this.notify.emit();
      }
    }
  }
}
